import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import moment from 'moment-timezone';
import _ from 'lodash';
import Textarea from 'react-textarea-autosize';
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
} from 'reactstrap';
import Flatpickr from 'react-flatpickr';
import { injectIntl } from 'react-intl';

import 'flatpickr/dist/themes/airbnb.css';

import { showModalTop } from '../../actions';
import {
  addCollectionToTree,
  hideCollectionEditModal,
  updateCollectionOnCollectionsList as updateCollectionOnCollectionsListAction,
  deleteCollectionFromCollectionsList,
  fetchFeaturedCollections,
} from '../../actions/Collections';
import { clearFiles, refreshFiles } from '../../actions/Files';

import {
  APIgetCollection,
  APIcreateCollection,
  APIupdateCollection,
} from '../../api/Collections';

import CollectionsTree from './Tree';
import CollectionsAccess from './Access';
import AccessModalAccessType from '../Access/ModalAccessType';
import ProcessingSpinner from '../ProcessingSpinner';

import {
  scrollModalToError,
  sendTrackingEvent,
} from '../../helpers';
import { isArchive } from '../../helpers/App';
import {
  userCan,
  userIsAdmin,
  userIsManager,
} from '../../helpers/Auth';
import { getParentCollection } from '../../helpers/Collections';
import {
  getFormattedDateTime,
  getRawDate,
  getRawUTCDate,
  getUserTimeZone,
} from '../../helpers/DateTime';
import { eventDispatch } from '../../helpers/Events';
import { addFlashMessage } from '../../helpers/FlashMessages';
import { performOnboardingStep } from '../../helpers/Onboarding';
import { getSetting } from '../../helpers/Settings';
import { prepareSearchFilters } from '../../helpers/URLParams';

import ErrorBoundary from '../ErrorBoundary';
import HelpIcon from '../HelpIcon';

const preventInput = (event) => {
  event.preventDefault();
  return false;
};

class CollectionEditModal extends Component {
  constructor(props) {
    super(props);

    this.state = this.getInitialState();

    this.handleChange = this.handleChange.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.handleSaveCollection = this.handleSaveCollection.bind(this);
    this.handleCloseModal = this.handleCloseModal.bind(this);
    this.handleSmart = this.handleSmart.bind(this);
    this.handleFiltersModal = this.handleFiltersModal.bind(this);
  }

  getPC() {
    const {
      collection,
      parentCollection,
    } = this.props;

    let pc = null;
    if (collection) {
      pc = parentCollection ? parentCollection.uuid : null;
    } else if (getSetting('cs1') && !userIsAdmin()) {
      pc = '-1';
    } else {
      pc = parentCollection ? parentCollection.uuid : null;
    }

    return pc;
  }

  getInitialState() {
    const pc = this.getPC();

    let defaultCollectionAccessType = getSetting('default_collection_access_type');
    if (defaultCollectionAccessType === 'public' && !userCan('collection.create_public')) {
      defaultCollectionAccessType = 'private';
    }

    return {
      parentCollectionUuid: pc,
      parentCollection: null,
      parentCollectionLoading: true,
      collectionName: '',
      collectionDescription: '',
      collectionAccess: defaultCollectionAccessType,
      collectionCopyAccessFromParent: (
        pc && pc !== '-1' && (
          getSetting('default_collection_access_type') !== 'public' &&
          userCan('collection.create_public')
        )
      ) ? null : false,
      collectionAllowChanges: !getSetting('cs1'),
      collectionFeatured: false,
      collectionCreatedAt: null,
      copyAccessInvalid: false,
      smart: false,
      selectedFilters: [],
      errors: {},
      saving: false,
    };
  }

  componentDidMount() {
    this.initialize();
  }

  componentDidUpdate(prevProps, prevState) {
    const { parentCollectionUuid } = this.state;
    const { parentCollectionUuid: prevParentCollectionUuid } = prevState;

    if (parentCollectionUuid && parentCollectionUuid !== prevParentCollectionUuid) {
      this.loadParentCollection();
    }
  }

  loadParentCollection() {
    const { parentCollectionUuid } = this.state;

    this.setState({ parentCollection: null });

    if (
      !parentCollectionUuid ||
      parentCollectionUuid === '-1' ||
      parentCollectionUuid === 'all'
    ) {
      this.setState({ parentCollectionLoading: false });
      return;
    }

    APIgetCollection(parentCollectionUuid, { skip_descendants: true }).then((response) => {
      if (response && response.data) {
        this.setState({ parentCollection: response.data });
      }
      this.setState({ parentCollectionLoading: false });
    });
  }

  initialize() {
    const {
      collection,
      parentCollection,
      smart,
      selectedFilters,
    } = this.props;

    if (collection) {
      let collectionAccess = 'private';
      if (collection.public) {
        collectionAccess = 'public';
      }
      if (collection.restricted) {
        collectionAccess = 'restricted';
      }
      this.setState({
        parentCollectionUuid: parentCollection ? parentCollection.uuid : null,
        parentCollection: null,
        collectionName: collection.name,
        collectionDescription: collection.description,
        collectionAccess,
        collectionCopyAccessFromParent: (
          collection.copy_access_from_parent === null && collection.public
        ) ? false : collection.copy_access_from_parent,
        collectionAllowChanges: collection.allow_changes,
        collectionFeatured: collection.featured,
        collectionCreatedAt: (getSetting('cs1') || getSetting('custom_collection_date')) ? collection.created_at : null,
        selectedFilters: collection.attributes?.filters || [],
        smart: collection.smart,
      }, () => {
        this.loadParentCollection();
      });
    } else {
      this.setState({
        parentCollectionUuid: parentCollection ? parentCollection.uuid : null,
        smart,
        selectedFilters,
      }, () => {
        this.loadParentCollection();
      });
    }

    if (!collection && parentCollection && parentCollection.uuid !== '-1') {
      sendTrackingEvent('collection-new-modal-open-with-parent');
    } else if (collection) {
      sendTrackingEvent('collection-edit-modal-open');
    } else {
      sendTrackingEvent('collection-new-modal-open');
    }
  }

  validateForm() {
    const { intl, collection } = this.props;
    const {
      collectionName,
      collectionCopyAccessFromParent,
      smart,
      selectedFilters,
    } = this.state;
    const errors = {};

    if (!collectionName) {
      errors.collectionName = intl.formatMessage({ defaultMessage: 'This field is required' });
    }

    if (smart && selectedFilters.length === 0) {
      errors.emptySmartSelectedFilters = intl.formatMessage({ defaultMessage: 'Please select Collection filters' });
    }

    this.setState({
      errors,
      copyAccessInvalid: !collection && collectionCopyAccessFromParent === null,
    });

    if (
      Object.keys(errors).length > 0 ||
      (!collection && collectionCopyAccessFromParent === null)
    ) {
      scrollModalToError('modal-edit-collection');
      return false;
    }

    return true;
  }

  handleChange(e) {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    if (type === 'checkbox') {
      this.setState({ [name]: checked });
    } else if (name === 'collectionCopyAccessFromParent') {
      this.setState({ [name]: !!(value === '1') });
    } else if (name === 'collectionCreatedAt') {
      this.setState({ [name]: `${value} 12:00:00` });
    } else {
      this.setState({ [name]: value });
    }

    if (name === 'collectionAccess') {
      const pc = this.getPC();
      this.setState({
        collectionCopyAccessFromParent: (
          value === 'public' ||
          (!pc || pc === '-1') ||
          !userCan('collection.create_public')
        ) ? false : null,
      });
    }
  }

  handleKeyPress(e) {
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
      e.preventDefault();
      this.handleSaveCollection();
    }
  }

  handleSaveCollection() {
    if (!this.validateForm()) {
      return;
    }

    const {
      parentCollectionUuid,
      collectionName,
      collectionDescription,
      collectionAccess,
      collectionCopyAccessFromParent,
      collectionAllowChanges,
      collectionFeatured,
      collectionCreatedAt,
      smart,
      selectedFilters,
    } = this.state;
    const {
      collection,
      afterCreateCollection,
      handleClose,
      filesGroup,
      notificationCallback,
      archived,
      addCollectionToTreeAction,
      updateCollectionOnCollectionsList,
      removeCollectionFromCollectionsList,
      loadFeaturedCollections,
      inOverlay,
      loadedCollection,
      handleRefreshFiles,
    } = this.props;

    this.setState({ saving: true });

    const createdAt = collectionCreatedAt ? getRawUTCDate(collectionCreatedAt).format('YYYY-MM-DD HH:mm:ss') : null;

    const collectionSelectedFilters = () => {
      let filters = [...selectedFilters];

      if (!filters.find(f => f.type === 'search')) {
        filters = filters.filter(f => f.type !== 'query_type');
      }

      return filters;
    };

    if (collection) {
      const updatedCollection = Object.assign({}, collection);
      updatedCollection.parent_uuid = parentCollectionUuid;
      updatedCollection.name = collectionName;
      updatedCollection.description = collectionDescription;
      updatedCollection.public = collectionAccess === 'public';
      updatedCollection.restricted = collectionAccess === 'restricted';
      updatedCollection.copy_access_from_parent = collectionCopyAccessFromParent;
      updatedCollection.allow_changes = collectionAllowChanges;
      updatedCollection.featured = collectionFeatured;
      updatedCollection.created_at = createdAt;
      updatedCollection.filters = collectionSelectedFilters();

      if (collection.is_empty) {
        updatedCollection.smart = smart;
        updatedCollection.filters = collectionSelectedFilters();
      }

      APIupdateCollection({ collection: updatedCollection }).then((response) => {
        if (response.data) {
          notificationCallback(response.data);
          const parentCollection = getParentCollection(collection);
          const newParentCollection = getParentCollection(response.data);
          if (
            (!parentCollection && newParentCollection) ||
            (parentCollection && !newParentCollection) ||
            (
              parentCollection && newParentCollection &&
              parentCollection.uuid !== newParentCollection.uuid
            )
          ) {
            removeCollectionFromCollectionsList(response.data);
          } else {
            updateCollectionOnCollectionsList(response.data);
          }
          afterCreateCollection(response.data);
          if (collection.featured !== response.data.featured) {
            loadFeaturedCollections();
          }
          if (loadedCollection?.uuid === collection.uuid && collection.smart) {
            handleRefreshFiles();
          }

          eventDispatch('collectionUpdated', response.data);
          eventDispatch('collectionUpdatedTree', response.data);
        }
        this.setState(this.getInitialState());
        handleClose();
      });
    } else {
      APIcreateCollection({
        parent_uuid: parentCollectionUuid,
        name: collectionName,
        description: collectionDescription,
        public: collectionAccess === 'public',
        restricted: collectionAccess === 'restricted',
        copy_access_from_parent: collectionCopyAccessFromParent,
        file_group_uuid: filesGroup ? filesGroup.uuid : null,
        allow_changes: collectionAllowChanges,
        featured: collectionFeatured,
        created_at: createdAt,
        archived,
        smart,
        filters: collectionSelectedFilters(),
      }).then((response) => {
        notificationCallback(response.data);
        if (response.data && response.data.uuid) {
          addCollectionToTreeAction({
            index: response.data.uuid,
            uuid: response.data.uuid,
            name: response.data.name,
            smart: response.data.smart,
            hasChildren: true,
            children: [],
            parentUuid: parentCollectionUuid,
          }, inOverlay ? 'overlay' : 'tree');
          addCollectionToTreeAction({
            index: response.data.uuid,
            uuid: response.data.uuid,
            name: response.data.name,
            smart: response.data.smart,
            hasChildren: true,
            children: [],
            parentUuid: parentCollectionUuid === 'all' ? null : parentCollectionUuid,
          }, 'sidebar');
          afterCreateCollection(response.data);
          if (response.data.featured) {
            loadFeaturedCollections();
          }
          eventDispatch('collectionCreated', response.data);
        }

        sendTrackingEvent('collection-created', { smart });
        performOnboardingStep('collection-created');

        this.setState(this.getInitialState());
        handleClose();
      }).catch((e) => {
        if (e.response.data.message) {
          addFlashMessage({
            type: 'danger',
            text: e.response.data.message,
          });
          this.setState({ saving: false });
        }
      });
    }
  }

  handleCloseModal() {
    const { closeCollectionEditModal } = this.props;
    closeCollectionEditModal();
  }

  handleSmart(e) {
    this.setState({
      smart: e.target.checked,
    });
  }

  handleFiltersModal() {
    const { openFiltersSelectionModal, collection, loadedCollection } = this.props;
    const { selectedFilters, errors } = this.state;

    const uniqueFilters = _.uniqBy([
      ...(collection?.attributes?.filters || []),
      ...(loadedCollection?.attributes?.filters || []),
    ], item => JSON.stringify(item));

    const initialFilters =
      collection && collection.smart
        ? prepareSearchFilters(uniqueFilters)
        : null;

    openFiltersSelectionModal({
      handleSaveFilters: (filters) => this.setState(
        { selectedFilters: filters },
        () => {
          if (errors.emptySmartSelectedFilters !== undefined) {
            this.validateForm();
          }
        },
      ),
      initialSelectedFilters: selectedFilters,
      initialFilters,
      className: 'modal-filters-selection',
    });
  }

  render() {
    const {
      parentCollectionUuid,
      parentCollection,
      parentCollectionLoading,
      collectionName,
      collectionDescription,
      collectionAccess,
      collectionCopyAccessFromParent,
      collectionAllowChanges,
      collectionFeatured,
      collectionCreatedAt,
      copyAccessInvalid,
      selectedFilters,
      smart,
      errors,
      saving,
    } = this.state;
    const {
      intl,
      collection,
      filesGroup,
      handleClose,
      archived,
      inOverlay,
      showSmartSection,
    } = this.props;

    return (
      <ErrorBoundary>
        <ModalHeader toggle={handleClose} className="modal-header-center">
          {collection ? (
            intl.formatMessage({ defaultMessage: 'Edit Collection' })
          ) : (
            intl.formatMessage({ defaultMessage: 'New Collection' })
          )}
        </ModalHeader>
        <ModalBody>
          {(filesGroup && filesGroup.type === 'upload') && (
            <p>
              {intl.formatMessage({
                defaultMessage: `You are creating a new Collection with {count}
                  {count, plural, one {file} other {files}} uploaded on {createdAt} <strong>by {user}</strong>`,
              }, {
                count: filesGroup.total_files,
                createdAt: getFormattedDateTime(filesGroup.created_at),
                user: filesGroup.user ? ` by ${filesGroup.user.name}` : '',
                strong: (chunks) => filesGroup?.user && <strong>{chunks}</strong>,
              })}
            </p>
          )}
          {(archived || (collection && collection.archived)) && (
            <p className="center">
              <strong>
                { collection ? intl.formatMessage({ defaultMessage: 'You are editing a Collection in the archive' }) :
                  intl.formatMessage({ defaultMessage: 'You are creating a Collection in the archive' }) }
              </strong>
            </p>
          )}
          <Form
            className="new-collection-form"
            onKeyPress={this.handleKeyPress}
          >
            <FormGroup>
              <Label for="inpCollectionName">
                {intl.formatMessage({ defaultMessage: 'Collection name' })}
              </Label>
              <Input
                type="text"
                name="collectionName"
                id="inpCollectionName"
                placeholder={intl.formatMessage({ defaultMessage: 'Collection name' })}
                autoFocus={true}
                value={collectionName}
                onChange={this.handleChange}
                invalid={errors.collectionName !== undefined}
              />
              <FormFeedback>
                {errors.collectionName}
              </FormFeedback>
            </FormGroup>
            <FormGroup>
              <Label for="inpCollectionDescription">
                {intl.formatMessage({ defaultMessage: 'Collection description' })}
              </Label>
              <Textarea
                name="collectionDescription"
                id="inpCollectionDescription"
                className="form-control"
                placeholder={intl.formatMessage({ defaultMessage: 'Collection description' })}
                value={collectionDescription || ''}
                onChange={this.handleChange}
              />
              <FormFeedback>
                {errors.collectionName}
              </FormFeedback>
            </FormGroup>
            {(getSetting('cs1') || getSetting('custom_collection_date')) && (
              <FormGroup>
                <Label for="collectionCreatedAt">
                  {intl.formatMessage({ defaultMessage: 'Collection date' })}
                </Label>
                <Flatpickr
                  id="collectionCreatedAt"
                  render={
                    ({ defaultValue, value, ...props }, ref) => (
                      <Input {...props} defaultValue={defaultValue} innerRef={ref} />
                    )
                  }
                  value={collectionCreatedAt}
                  onReady={(dates, dateStr, instance) => {
                    const el = instance.element;
                    el.onkeypress = preventInput;
                    el.onkeydown = preventInput;
                    el.onkeyup = preventInput;
                  }}
                  options={{
                    allowInput: true,
                    now: getRawDate(moment().tz(getUserTimeZone())),
                  }}
                  onChange={(dates, date) => {
                    this.handleChange({ target: { name: 'collectionCreatedAt', value: date } });
                  }}
                />
              </FormGroup>
            )}
            {showSmartSection && !(collection && !collection.smart && !collection.is_empty) && (
              <FormGroup className="modal-edit-smart-section">
                <div>
                  <Label htmlFor="collection-smart" className={classnames('switch', { disabled: collection && collection.smart })}>
                    <div className="switch-control input-inner">
                      <Input
                        type="checkbox"
                        name="collection-smart"
                        id="collection-smart"
                        value={smart}
                        checked={smart}
                        onChange={(e) => this.handleSmart(e)}
                        disabled={collection && collection.smart}
                      />
                      <i />
                    </div>
                    {intl.formatMessage({ defaultMessage: 'Smart' })}
                    {smart && (
                    <span className="text-glow-violet">
                      <i className="fas fa-bolt" />
                    </span>
                    )}
                    <HelpIcon id="smart-collections" permanent={true}>
                      {intl.formatMessage({
                        defaultMessage:
                        'Add files automatically based on selected file filters',
                      })}
                    </HelpIcon>
                  </Label>
                  <Button size="sm" onClick={this.handleFiltersModal} color="link" className={classnames('adjust-filters-button', { invisible: !smart })}>
                    {intl.formatMessage({ defaultMessage: 'Select filters' })}
                    {selectedFilters.length > 0 && <Badge color="primary">{' '}</Badge>}
                  </Button>
                </div>
                <FormFeedback>
                  {errors.emptySmartSelectedFilters}
                </FormFeedback>
              </FormGroup>
            )}
            <FormGroup className="parent-collection">
              <Label htmlFor="collections-tree">
                {intl.formatMessage({ defaultMessage: 'Parent Collection' })}
              </Label>
              <CollectionsTree
                selectedCollections={parentCollectionUuid ? [parentCollectionUuid] : ['all']}
                hiddenCollections={collection ? [collection.uuid] : []}
                enableAddNew={false}
                enableAllCollections={true}
                onlyMyCollections={getSetting('cs1') && !userIsAdmin()}
                onlyOpenCollections={true}
                source={inOverlay ? 'overlay' : 'tree'}
                onChange={(selectedUuids) => {
                  if (selectedUuids.length > 0) {
                    this.setState({ parentCollectionUuid: selectedUuids[0] });
                  } else {
                    this.setState({
                      parentCollectionUuid: null,
                      parentCollection: null,
                    });
                  }
                }}
                smartCollectionsRestrict={false}
              />
            </FormGroup>
            {!collection && (
              <FormGroup className="access-type collection-access">
                <h5>{intl.formatMessage({ defaultMessage: 'Collection Visibility' })}</h5>
                <AccessModalAccessType
                  mode="collections"
                  accessType={collectionAccess}
                  radioName="collectionAccess"
                  labels="tooltips"
                  handleChangeAccessType={this.handleChange}
                />
                {(collectionAccess === 'private' && (
                  (userCan('collection.create_public') && !collectionCopyAccessFromParent) ||
                  (parentCollectionUuid && parentCollectionUuid !== '-1')
                )) && (
                  <>
                    {(
                      !parentCollectionLoading &&
                      parentCollectionUuid &&
                      parentCollectionUuid !== '-1' &&
                      parentCollectionUuid !== 'all' &&
                      !(parentCollection && parentCollection.public && !userCan('collection.create_public'))
                    ) && (
                      <FormGroup check className={classnames('copy-access-from-parent', { 'is-invalid': copyAccessInvalid })}>
                        <Label check>
                          <Input
                            type="radio"
                            name="collectionCopyAccessFromParent"
                            className="input-radio no-margin"
                            value="0"
                            disabled={saving}
                            defaultChecked={collection ?
                              collectionCopyAccessFromParent === false : false}
                            onChange={this.handleChange}
                          />
                          {' '}
                          {intl.formatMessage({ defaultMessage: 'Only visible to me' })}
                        </Label>
                        <Label check>
                          <Input
                            type="radio"
                            name="collectionCopyAccessFromParent"
                            className="input-radio no-margin"
                            value="1"
                            disabled={saving}
                            defaultChecked={collection ?
                              collectionCopyAccessFromParent === true : false}
                            onChange={this.handleChange}
                          />
                          {' '}
                          {intl.formatMessage({ defaultMessage: 'Copy visibility from parent Collection' })}
                          {parentCollection && (
                            <span className="collection-details">
                              <div>
                                <CollectionsAccess
                                  collection={parentCollection}
                                  inModal={true}
                                />
                              </div>
                            </span>
                          )}
                        </Label>
                        <FormFeedback style={{ display: copyAccessInvalid ? 'block' : 'none' }}>
                          {intl.formatMessage({ defaultMessage: 'Please select visibility' })}
                        </FormFeedback>
                      </FormGroup>
                    )}
                  </>
                )}
              </FormGroup>
            )}
            <FormGroup check className="allow-changes">
              <Label for="collectionAllowChanges" check>
                <Input
                  type="checkbox"
                  id="collectionAllowChanges"
                  name="collectionAllowChanges"
                  className="input-check check-s form-check-input no-margin"
                  value="1"
                  disabled={saving}
                  checked={collectionAllowChanges}
                  onChange={this.handleChange}
                />
                {' '}
                {intl.formatMessage({ defaultMessage: 'Allow users with access to add/remove files' })}
              </Label>
            </FormGroup>
            {(userIsAdmin() || userIsManager()) && (
              <FormGroup check className="show-in-sidebar">
                <Label for="collectionFeatured" check>
                  <Input
                    type="checkbox"
                    id="collectionFeatured"
                    name="collectionFeatured"
                    className="input-check check-s form-check-input no-margin"
                    value="1"
                    disabled={saving}
                    checked={collectionFeatured}
                    onChange={this.handleChange}
                  />
                  {' '}
                  {intl.formatMessage({ defaultMessage: 'Feature Collection in sidebar' })}
                </Label>
              </FormGroup>
            )}
          </Form>
        </ModalBody>
        <ModalFooter>
          <div className="buttons-group">
            <Button
              color="secondary"
              disabled={saving}
              onClick={handleClose}
            >
              {intl.formatMessage({ defaultMessage: 'Cancel' })}
            </Button>
            <Button
              color="primary"
              disabled={saving}
              onClick={this.handleSaveCollection}
            >
              <ProcessingSpinner show={saving} />
              {intl.formatMessage({ defaultMessage: 'Save' })}
            </Button>
          </div>
        </ModalFooter>
      </ErrorBoundary>
    );
  }
}

const mapStateToProps = state => ({
  collectionEditModal: state.Collections.editModal,
  loadedCollection: state.Collections.collection,
});

const mapDispatchToProps = dispatch => ({
  closeCollectionEditModal: () => {
    dispatch(hideCollectionEditModal());
  },
  addCollectionToTreeAction: (collection, source) => {
    dispatch(addCollectionToTree(collection, source));
  },
  updateCollectionOnCollectionsList: (collection) => {
    dispatch(updateCollectionOnCollectionsListAction(collection));
  },
  removeCollectionFromCollectionsList: (collection) => {
    dispatch(deleteCollectionFromCollectionsList(collection));
  },
  loadFeaturedCollections: () => {
    if (isArchive()) {
      dispatch(fetchFeaturedCollections({ archived: true }));
    } else {
      dispatch(fetchFeaturedCollections());
    }
  },
  openFiltersSelectionModal: (params) => {
    dispatch(showModalTop('FiltersSelectionModal', params));
  },
  handleRefreshFiles: () => {
    dispatch(clearFiles());
    dispatch(refreshFiles());
  },
});

CollectionEditModal.propTypes = {
  intl: PropTypes.object.isRequired,
  collection: PropTypes.object,
  parentCollection: PropTypes.object,
  closeCollectionEditModal: PropTypes.func.isRequired,
  addCollectionToTreeAction: PropTypes.func.isRequired,
  updateCollectionOnCollectionsList: PropTypes.func.isRequired,
  removeCollectionFromCollectionsList: PropTypes.func.isRequired,
  loadFeaturedCollections: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  afterCreateCollection: PropTypes.func,
  filesGroup: PropTypes.object,
  notificationCallback: PropTypes.func,
  archived: PropTypes.bool,
  inOverlay: PropTypes.bool,
  top: PropTypes.bool,
  smart: PropTypes.bool,
  selectedFilters: PropTypes.array,
  openFiltersSelectionModal: PropTypes.func.isRequired,
  loadedCollection: PropTypes.object,
  showSmartSection: PropTypes.bool,
  handleRefreshFiles: PropTypes.func.isRequired,
};

CollectionEditModal.defaultProps = {
  loadedCollection: null,
  collection: null,
  parentCollection: null,
  filesGroup: null,
  archived: false,
  inOverlay: false,
  top: false,
  smart: false,
  showSmartSection: true,
  selectedFilters: [],
  afterCreateCollection: () => {},
  notificationCallback: () => {},
};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(CollectionEditModal));
